import { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ConfigProvider } from 'antd'
import { Formik, FormikProps } from 'formik'
import * as Yup from 'yup'

import getReimbursementsDueApi, {
  EmployeeReimbursementsDue,
} from 'core/application/adminDashboard/reimbursements/reimbursementsDue/getReimbursementsDue'
import submitReimbursementsDue, {
  ReimbursementsDueValues,
  UserReimbursementRequest,
} from 'core/application/adminDashboard/reimbursements/reimbursementsDue/submitReimbursementsDue'
import { getReimbursementsHistoryAsCsv } from 'core/application/adminDashboard/reimbursements/reimbursementsHistory/getReimbursementsHistory'
import { ReimbursementMethod } from 'core/domain/expenses/reimbursementMethod'

import { PRESENTED_IMAGE_SIMPLE } from 'common/components/dataDisplay/Empty'

import { useLoginUserV2 } from 'common/hooks/useLoginUser'

import { Button, Col, DatePicker, Empty, Notification, Row, SimpleDatepicker, Table, Tooltip } from 'common/components'
import { submitDateToServerFormat } from 'common/dateFormatters'
import { downloadCSV } from 'common/downloadFile'
import { formatMoney } from 'common/moneyFormatter'

import { useCommandRequest, useQueryRequest, useQueryRequestV2 } from '../../../../common/hooks'
import { reimbursementsDueColumns } from './ReimbursementsDueColumns'
import ApproveReimbursementsDueModal from './approveReimbursementsDueModal'
import SelectReimburesementMethod, { reimbursementMethodsTitleMap } from './selectReimbursementMethod'
import TotalReimbursementsDue from './totalReimbursementsDue'

import './ReimbursementsDue.less'

const formValidator = (translate: (key: string) => string) =>
  Yup.object({
    scheduled: Yup.date().typeError(translate('general.required')).required(translate('general.required')),
    reimbursementMethodSelect: Yup.string().nullable(),
    reimbursementMethodOverride: Yup.string()
      .nullable()
      .when('reimbursementMethodSelect', {
        is: (reimbursementMethodSelect: string) => reimbursementMethodSelect === null,
        then: Yup.string().trim().required(translate('general.required')).typeError(translate('general.required')),
      }),
  })

enum SelectOptionOrOverride {
  select = 0,
  override = 1,
}

interface ReimbursementsDueProps {
  planPeriodId: string
}

export interface ReimbursementsDueToSubmit {
  companyUserId: string
  originalReimbursementDue: number
  overrideReimbursementDue?: number | undefined
}

export interface DropdownVisible {
  key: string
  visible: boolean
}

const getSelectedReimbursementMethodAsString = (formik: FormikProps<any>): string => {
  if (formik.values.reimbursementMethodSelect === null) {
    return formik.values.reimbursementMethodOverride
  } else {
    return reimbursementMethodsTitleMap.get(formik.values.reimbursementMethodSelect) ?? ''
  }
}

const getSelectedReimbursementMethod = (formik: FormikProps<any>): ReimbursementMethod => {
  return formik.values.reimbursementMethodSelect
}

const getTotalSelectedReimbursementsDue = (
  reimbursementsDueToSubmit: ReimbursementsDueToSubmit[],
  selectedReimbursementsDueToSubmit: string[] | number[],
): string => {
  const total = reimbursementsDueToSubmit
    .filter((item) => selectedReimbursementsDueToSubmit.some((s: string | number) => s === item.companyUserId))
    .reduce(
      (total, next) =>
        total + (next.overrideReimbursementDue ? next.overrideReimbursementDue : next.originalReimbursementDue),
      0,
    )

  return formatMoney(total)
}

const ReimbursementsDue: React.FC<ReimbursementsDueProps> = ({ planPeriodId }) => {
  const { t: translate } = useTranslation()
  const { user } = useLoginUserV2()
  const [reimbursementsDueToSubmit, setReimbursementsDueToSubmit] = useState<ReimbursementsDueToSubmit[]>([])
  const [selectedReimbursementsDueToSubmit, setSelectedReimbursementsDueToSubmit] = useState<string[] | number[]>([])
  const [overrideReimbursementsDueDropdownVisible, setOverrideReimbursementsDueDropdownVisible] = useState<
    DropdownVisible[]
  >([])
  const [reimbursementsThrough, setReimbursementsThrough] = useState<string>(null)
  const [approveLoading, setApproveLoading] = useState<boolean>(false)
  const [approveModalVisible, setApproveModalVisible] = useState<boolean>(false)
  const [sendCommand, isCommandLoading] = useCommandRequest()
  const [csvData, isCsvDataLoading, sendGetCsvData] = useQueryRequest()
  const {
    sendQuery: sendGetReimbursementsDue,
    data: reimbursementsDue,
    isLoading: loadingReimbursementsDue,
    reload: getReimbursementsDue,
  } = useQueryRequestV2<EmployeeReimbursementsDue[]>({
    initialState: [],
    queryFn: () => getReimbursementsDueApi({ planPeriodId: planPeriodId, dateThrough: reimbursementsThrough }),
    enable: !!planPeriodId,
  })

  useEffect(() => {
    ;(async () => {
      const result = await sendGetReimbursementsDue(() =>
        getReimbursementsDueApi({ planPeriodId, dateThrough: reimbursementsThrough }),
      )
      setReimbursementsDueToSubmit(
        result.map((item: EmployeeReimbursementsDue) => {
          return {
            companyUserId: item.companyUserId,
            originalReimbursementDue: item.reimbursementsDue,
          }
        }),
      )

      setOverrideReimbursementsDueDropdownVisible(
        result.map((item: EmployeeReimbursementsDue) => {
          return {
            key: item.companyUserId,
            visible: false,
          }
        }),
      )
    })()
  }, [planPeriodId, reimbursementsThrough])

  let reimbursementMethodSelectOption: SelectOptionOrOverride | null = null

  const reimbursementMethod = (formik: FormikProps<any>) => {
    const overrideFieldDefaultValue = null
    const selectFieldDefaultValue = null

    if (
      formik.values.reimbursementMethodOverride !== overrideFieldDefaultValue &&
      reimbursementMethodSelectOption !== SelectOptionOrOverride.override
    ) {
      reimbursementMethodSelectOption = SelectOptionOrOverride.override
      formik.setFieldValue('reimbursementMethodSelect', selectFieldDefaultValue)
      return null
    } else if (
      formik.values.reimbursementMethodSelect !== selectFieldDefaultValue &&
      reimbursementMethodSelectOption !== SelectOptionOrOverride.select
    ) {
      reimbursementMethodSelectOption = SelectOptionOrOverride.select
      formik.setFieldValue('reimbursementMethodOverride', overrideFieldDefaultValue)
    }
    return null
  }

  const rowSelection = {
    onChange: (selectedRowKeys: string[] | number[], selectedRows: EmployeeReimbursementsDue[]) => {
      setSelectedReimbursementsDueToSubmit(selectedRowKeys)
    },
  }

  const setDropdownVisibility = (key: string, visible: boolean): void => {
    const overrideReimbursementsDueDropdownVisibleCopy = [...overrideReimbursementsDueDropdownVisible]
    const dropdownItem = overrideReimbursementsDueDropdownVisibleCopy.find((item) => item.key === key)
    if (dropdownItem) {
      dropdownItem.visible = visible
      setOverrideReimbursementsDueDropdownVisible(overrideReimbursementsDueDropdownVisibleCopy)
    }
  }

  const selectedReimbursementsDue = (key: string, amount: number): void => {
    const reimbursementsDueToSubmitCopy = [...reimbursementsDueToSubmit]
    const companyUser = reimbursementsDueToSubmitCopy.find((item) => item.companyUserId === key)
    if (companyUser) {
      if (companyUser.originalReimbursementDue === amount) {
        companyUser.overrideReimbursementDue = undefined
      } else {
        companyUser.overrideReimbursementDue = amount
      }
      setReimbursementsDueToSubmit(reimbursementsDueToSubmitCopy)
    }
  }

  const getTotalSelectedReimbursementsDueContent = (): ReactNode => {
    if (selectedReimbursementsDueToSubmit.length === 0) return null
    return (
      <div className="total_selected_amount p-16">
        <h6 className="pr-40">
          {translate('admin-dashboard.reimbursements.reimbursements-due.total-selected-label')}:
        </h6>{' '}
        <h6>{getTotalSelectedReimbursementsDue(reimbursementsDueToSubmit, selectedReimbursementsDueToSubmit)}</h6>
      </div>
    )
  }

  const getListOfReimbursementsDue = () => {
    return reimbursementsDueToSubmit
      .filter((item) => selectedReimbursementsDueToSubmit.some((s: any) => s === item.companyUserId))
      .map((item) => {
        var employee = reimbursementsDue.find((r) => r.companyUserId === item.companyUserId)
        return {
          amountToReimburse:
            item.overrideReimbursementDue !== undefined ? item.overrideReimbursementDue : item.originalReimbursementDue,
          employeeFullName: employee ? `${employee.firstName} ${employee.lastName}` : '',
        }
      })
  }

  const getSubmitButton = (formik: FormikProps<any>): ReactNode => {
    const valid = formik.isValid && selectedReimbursementsDueToSubmit.length > 0
    const button = (
      <Button
        type="primary"
        className="approve_button"
        icon="far fa-check-circle"
        htmlType="submit"
        disabled={!valid || isCommandLoading}
      >
        {translate('general.approve')}
      </Button>
    )

    if (!valid) {
      return (
        <Tooltip title={translate('admin-dashboard.reimbursements.reimbursements-due.approve-tooltip')} position="left">
          {button}
        </Tooltip>
      )
    } else {
      return button
    }
  }

  const onSubmit = (values: any) => {
    setApproveModalVisible(true)
  }

  const onApproveSubmit = async (downloadCsv: boolean, formik: FormikProps<any>) => {
    if (formik.isValid) {
      setApproveLoading(true)

      const valuesToSubmit: ReimbursementsDueValues = {
        planPeriodId: planPeriodId,
        scheduledDate: formik.values.scheduled,
        reimbursementMethod: getSelectedReimbursementMethod(formik),
        otherReimbursementMethod: formik.values.reimbursementMethodOverride,
        userReimbursements: getUsersToReimburse(),
        reimbursementsThrough: reimbursementsThrough,
      }

      const result = await sendCommand(() => submitReimbursementsDue(valuesToSubmit))
      if (result.completed) {
        if (downloadCsv) {
          const csvResult = await sendGetCsvData(() =>
            getReimbursementsHistoryAsCsv(planPeriodId, result.data.toString()),
          )
          downloadCSV(csvResult, 'Reimbursements_history.csv')
        }

        Notification.open({
          message: translate('admin-dashboard.reimbursements.reimbursements-due.reimbursements-due-success-message'),
          type: 'success',
        })

        await getReimbursementsDue()
      }
      setApproveLoading(false)
      setApproveModalVisible(false)
    } else {
      setApproveLoading(false)
      setApproveModalVisible(false)
    }
  }

  const getUsersToReimburse = (): UserReimbursementRequest[] => {
    const result = []

    selectedReimbursementsDueToSubmit.forEach((companyUserId: string | number) => {
      const amountToReimburse = reimbursementsDueToSubmit.find((item) => item.companyUserId === companyUserId)
      result.push({
        companyUserId: companyUserId,
        overrideReimbursementDue: amountToReimburse.overrideReimbursementDue ?? null,
      })
    })

    return result
  }

  const handleDateChange = (value: any) => {
    setReimbursementsThrough(value === null ? null : submitDateToServerFormat(value))
  }

  return (
    <Row className="reimbursements_due">
      <Col span={24}>
        <Row>
          <Col span={24}>
            <label className="text-semi-bold">
              {translate('admin-dashboard.reimbursements.reimbursements-due.reimbursements-through')}
            </label>
            <SimpleDatepicker className="ml-16" allowClear format="MM/DD/YYYY" onChange={handleDateChange} />
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Formik
              enableReinitialize={true}
              validationSchema={formValidator(translate)}
              initialValues={{}}
              onSubmit={onSubmit}
            >
              {(formik) => {
                return (
                  <form onSubmit={formik.handleSubmit} autoComplete="off" className="reimbursement_request">
                    <Row className="pb-16">
                      <Col span={24} className="pt-24">
                        <ApproveReimbursementsDueModal
                          translate={translate}
                          visible={approveModalVisible}
                          approveLoading={approveLoading}
                          onCancel={() => {
                            setApproveLoading(false)
                            setApproveModalVisible(false)
                          }}
                          onApprove={(downloadCsv) => onApproveSubmit(downloadCsv, formik)}
                          companyName={user.companyName}
                          scheduled={formik.values.scheduled}
                          reimbursementMethod={getSelectedReimbursementMethodAsString(formik)}
                          reimbursementsDue={getListOfReimbursementsDue()}
                        />

                        <ConfigProvider
                          renderEmpty={() => (
                            <Empty
                              description={translate(
                                'admin-dashboard.reimbursements.reimbursements-due.no-reimbursements-due-label',
                              )}
                              image={PRESENTED_IMAGE_SIMPLE}
                            />
                          )}
                        >
                          <Table
                            className={'t-reimbursement-due'}
                            dataSource={reimbursementsDue}
                            loading={loadingReimbursementsDue}
                            scroll={{ x: 1000 }}
                            columns={reimbursementsDueColumns(
                              formik,
                              translate,
                              reimbursementsDueToSubmit,
                              overrideReimbursementsDueDropdownVisible,
                              setDropdownVisibility,
                              selectedReimbursementsDue,
                              true,
                            )}
                            rowKey="companyUserId"
                            pagination={false}
                            footer={(data: EmployeeReimbursementsDue[]) => {
                              return <TotalReimbursementsDue reimbursementsDue={data} translate={translate} />
                            }}
                            rowSelection={rowSelection}
                          />
                        </ConfigProvider>

                        {getTotalSelectedReimbursementsDueContent()}
                      </Col>
                    </Row>

                    <Row className="payment_details" gutter={16} justify="end">
                      <Col xs={24} sm={24} md={5} lg={5} xl={5}>
                        <SelectReimburesementMethod translate={translate} />
                        {reimbursementMethod(formik)}
                      </Col>
                      <Col xs={24} sm={24} md={5} lg={5} xl={5}>
                        <DatePicker
                          label={translate('admin-dashboard.reimbursements.reimbursements-due.scheduled-label')}
                          name="scheduled"
                          id="scheduled"
                          noStyles
                          className="scheduled_datepicker"
                          format={'MM/DD/YYYY'}
                          disabledDate={(d) => !d}
                        />
                      </Col>
                      <Col xs={24} sm={24} md={5} lg={5} xl={4} className="approve_button mt-8">
                        {getSubmitButton(formik)}
                      </Col>
                    </Row>
                  </form>
                )
              }}
            </Formik>
          </Col>
        </Row>
      </Col>
    </Row>
  )
}

export default ReimbursementsDue
